import { useCallback, useState } from "react";
import { lumi } from "@/lib/lumi";
import type { ContractType, JobCategory } from "@/data/jobs";

/**
 * Hook de gestion des alertes emploi du candidat.
 * Critères simplifiés : Catégorie, Fréquence, Région.
 * Une alerte filtre les nouvelles offres par catégorie et région,
 * puis notifie le candidat par email selon la fréquence choisie
 * (immédiate, quotidienne, hebdomadaire).
 */

export type AlertCategory = JobCategory | "ALL";
export type AlertFrequency = "immediate" | "daily" | "weekly";

export interface JobAlert {
  _id: string;
  creator: string;
  creatorEmail?: string;
  name: string;
  category: AlertCategory;
  /** Région française (ex. "Île-de-France"). Vide = toutes régions. */
  region?: string;
  frequency: AlertFrequency;
  isActive: boolean;
  lastSentAt?: string;
  lastNotifiedJobIds?: string[];
  createdAt: string;
  updatedAt: string;
  /** Champs hérités, conservés pour compat mais non utilisés. */
  contractTypes?: ContractType[];
  location?: string;
  keywords?: string;
}

export interface NewJobAlert {
  name: string;
  category: AlertCategory;
  region?: string;
  frequency: AlertFrequency;
  isActive: boolean;
  /** Champs hérités, conservés pour compat. */
  contractTypes?: ContractType[];
  location?: string;
  keywords?: string;
}

const COLLECTION = "job_alerts";

export function useJobAlerts() {
  const [alerts, setAlerts] = useState<JobAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Charge les alertes du candidat connecté */
  const fetchMine = useCallback(async (userId: string) => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const { list } = await lumi.entities[COLLECTION].list({
        filter: { creator: userId },
        sort: { createdAt: -1 },
      });
      setAlerts((list as JobAlert[]) || []);
    } catch (e) {
      console.error("[useJobAlerts] fetchMine error", e);
      setError("Impossible de charger vos alertes.");
    } finally {
      setLoading(false);
    }
  }, []);

  /** Crée une nouvelle alerte */
  const createAlert = useCallback(
    async (
      userId: string,
      userEmail: string | undefined,
      data: NewJobAlert
    ): Promise<JobAlert | null> => {
      if (!userId) return null;
      const now = new Date().toISOString();
      try {
        const created = (await lumi.entities[COLLECTION].create({
          ...data,
          creator: userId,
          creatorEmail: userEmail || "",
          lastNotifiedJobIds: [],
          createdAt: now,
          updatedAt: now,
        })) as JobAlert;
        setAlerts((prev) => [created, ...prev]);
        return created;
      } catch (e) {
        console.error("[useJobAlerts] createAlert error", e);
        throw e;
      }
    },
    []
  );

  /** Met à jour une alerte (toggle, edit, etc.) */
  const updateAlert = useCallback(
    async (alertId: string, data: Partial<NewJobAlert> & { lastSentAt?: string; lastNotifiedJobIds?: string[] }) => {
      const now = new Date().toISOString();
      try {
        const updated = (await lumi.entities[COLLECTION].update(alertId, {
          ...data,
          updatedAt: now,
        })) as JobAlert;
        setAlerts((prev) => prev.map((a) => (a._id === alertId ? { ...a, ...updated } : a)));
        return updated;
      } catch (e) {
        console.error("[useJobAlerts] updateAlert error", e);
        throw e;
      }
    },
    []
  );

  /** Supprime une alerte */
  const deleteAlert = useCallback(async (alertId: string) => {
    try {
      await lumi.entities[COLLECTION].delete(alertId);
      setAlerts((prev) => prev.filter((a) => a._id !== alertId));
    } catch (e) {
      console.error("[useJobAlerts] deleteAlert error", e);
      throw e;
    }
  }, []);

  return {
    alerts,
    loading,
    error,
    fetchMine,
    createAlert,
    updateAlert,
    deleteAlert,
  };
}

/**
 * Vérifie si une offre matche les critères d'une alerte (Catégorie + Région).
 * La région est matchée en recherche partielle sur `location`/`department`/`region`.
 */
export function jobMatchesAlert(
  job: {
    id: string;
    title: string;
    description?: string;
    location?: string;
    department?: string;
    region?: string;
    category: JobCategory;
    contractType: ContractType;
  },
  alert: Pick<JobAlert, "category" | "region" | "contractTypes" | "location" | "keywords">
): boolean {
  // Catégorie
  if (alert.category !== "ALL" && job.category !== alert.category) return false;
  // Région (recherche partielle insensible à la casse, sur les champs géographiques de l'offre)
  if (alert.region && alert.region.trim()) {
    const needle = alert.region.trim().toLowerCase();
    const haystack =
      `${job.region || ""} ${job.location || ""} ${job.department || ""}`.toLowerCase();
    if (!haystack.includes(needle)) return false;
  }
  // Compat : si d'anciennes alertes ont encore des contractTypes/location/keywords,
  // on les respecte pour ne pas casser l'existant.
  if (alert.contractTypes && alert.contractTypes.length > 0) {
    if (!alert.contractTypes.includes(job.contractType)) return false;
  }
  if (alert.location && alert.location.trim()) {
    const needle = alert.location.trim().toLowerCase();
    const haystack = `${job.location || ""} ${job.department || ""}`.toLowerCase();
    if (!haystack.includes(needle)) return false;
  }
  if (alert.keywords && alert.keywords.trim()) {
    const needles = alert.keywords
      .toLowerCase()
      .split(/[\s,]+/)
      .filter(Boolean);
    const haystack = `${job.title} ${job.description || ""}`.toLowerCase();
    const hasOne = needles.some((n) => haystack.includes(n));
    if (!hasOne) return false;
  }
  return true;
}
